export default function BusSeatLayout({
  seats = [],
  selectedSeats = [],
  onSeatClick,
  mode = 'reserve',
  seatPreferences = [],
  totalSeats = 44,
}) {
  const cols = 4;
  const rows = Math.ceil(totalSeats / cols);

  const getSeatData = (seatNum) => {
    if (mode === 'reserve') {
      const seat = seats.find(s => Number(s.seatNo || s.number) === seatNum);
      const isSelected = selectedSeats.includes(seatNum);
      const isAvailable = seat ? (seat.available !== false && seat.reserved !== true) : false;
      return { isSelected, isAvailable, exists: !!seat };
    }
    const prefIndex = seatPreferences.indexOf(seatNum);
    return { isSelected: prefIndex >= 0, prefOrder: prefIndex + 1 };
  };

  const getSeatStyle = (seatNum) => {
    const data = getSeatData(seatNum);
    if (mode === 'reserve') {
      if (data.isSelected) return 'bg-tayo text-white shadow-md ring-2 ring-tayo/30 animate-pulse-glow';
      if (data.isAvailable) return 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer';
      return 'bg-gray-100 text-gray-300 border border-gray-100 cursor-not-allowed';
    }
    if (data.isSelected) return 'bg-tayo text-white shadow-md';
    return 'bg-gray-100 text-gray-400 border border-gray-200 hover:bg-gray-200 cursor-pointer';
  };

  const handleClick = (seatNum) => {
    if (mode === 'reserve') {
      const data = getSeatData(seatNum);
      if (!data.isAvailable && !data.isSelected) return;
    }
    onSeatClick?.(seatNum);
  };

  const renderSeat = (seatNum) => {
    if (seatNum > totalSeats) return <div key={`empty-${seatNum}`} className="w-10 h-10" />;
    const data = getSeatData(seatNum);

    return (
      <button
        key={seatNum}
        onClick={() => handleClick(seatNum)}
        className={`w-10 h-10 rounded-lg text-xs font-bold transition-all duration-150 active:scale-90 ${getSeatStyle(seatNum)}`}
      >
        {mode === 'preference' && data.isSelected ? data.prefOrder : seatNum}
      </button>
    );
  };

  return (
    <div className="flex flex-col items-center">
      {/* Bus shape container */}
      <div className="relative w-full max-w-[280px]">
        {/* Bus front */}
        <div className="bg-gray-50 border-2 border-gray-200 border-b-0 rounded-t-[2.5rem] px-6 pt-5 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
              </div>
              <span className="text-[10px] text-sub-text font-medium">운전석</span>
            </div>
            <div className="flex gap-1">
              <div className="w-3 h-6 bg-gray-200 rounded-sm" />
              <div className="w-3 h-6 bg-gray-200 rounded-sm" />
            </div>
          </div>
        </div>

        {/* Bus body (seats) */}
        <div className="bg-gray-50 border-x-2 border-gray-200 px-4 py-3">
          <div className="space-y-1.5">
            {Array.from({ length: rows }, (_, row) => (
              <div key={row} className="flex items-center justify-center gap-0.5">
                {renderSeat(row * cols + 1)}
                {renderSeat(row * cols + 2)}
                <div className="w-5 flex-shrink-0" />
                {renderSeat(row * cols + 3)}
                {renderSeat(row * cols + 4)}
              </div>
            ))}
          </div>
        </div>

        {/* Bus rear */}
        <div className="bg-gray-50 border-2 border-gray-200 border-t-0 rounded-b-xl h-4" />
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-sub-text">
        {mode === 'reserve' ? (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" />
              <span>선택 가능</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-100" />
              <span>예약됨</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-tayo" />
              <span>내 좌석</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-tayo" />
              <span>선호 좌석</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
              <span>미선택</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
